const rowIds = row => [row?.id, row?.idPrincipal].filter(id => id != null).map(String)
const rowIdentity = row => row?.idPrincipal != null ? `libro:${row.idPrincipal}` : `row:${row?.id}`
// The creation endpoint can return a template ID instead of an accounting ID.
// Restrict field matching to the requested template's folio in the listings.
const folioByModel = {
  'pago-arriendo': 0,
  'cobro-arriendo': 1,
  'gastos-comunes': 2,
  'retiro-dividendo': 3,
  'pago-patente-comercial': 8,
  'pago-contribuciones': 9,
  'aporte-transitorio-socio': 10,
  'devolucion-de-impuestos': 36,
  'devolucion-aporte-transitorio': 11,
  'rendicion-gastos': 12,
  'rendicion-gasto': 12,
  'ajuste-existencias': 22,
  'invoice-compra': 13,
  'invoice-venta': 14,
  'pago-finiquito': 15,
  'inversiones-dap': 16,
  'inversiones-ffmm': 17,
  'inversiones-otras': 18,
  'acciones-en-bolsa': 19,
  criptomonedas: 20,
  'otros-varoles-negociables': 21,
  'vinculo-generico-compra': 25,
  'vinculo-generico-venta': 26,
  'anticipo-agente-aduana': 27,
  'anticipo-sueldo': 28,
  multa: 29,
  'devolucion-agente-aduana': 30,
  'aporte-ctacte-empresa': 33,
  'devolucion-ctacte-empresa': 34,
  'invoice-importacion': 35
}

const hasAccountingIds = value => {
  if (!value || typeof value !== 'object') return false
  if (Array.isArray(value)) return true
  if (['idPrincipal', 'idLibro', 'libroId', 'libroContableId', 'ids'].some(key => value[key] != null)) return true
  return ['data', 'libro', 'libros', 'movimiento', 'movimientos', 'result'].some(key => hasAccountingIds(value[key]))
}

export const getCreatedResponseIds = response => {
  const ids = new Set()
  const visit = value => {
    if ((typeof value === 'number' || typeof value === 'string') && /^\d+$/.test(String(value)) && Number(value) > 0) {
      ids.add(String(value))
      return
    }
    if (!value || typeof value !== 'object') return
    if (Array.isArray(value)) return value.forEach(visit)
    // Prefer the accounting ID; a response can also include an unrelated summary ID.
    const accountingId = ['idPrincipal', 'idLibro', 'libroId', 'libroContableId']
      .map(key => value[key]).find(id => id != null && Number(id) > 0)
    const id = accountingId ?? value.id
    if (id != null && Number(id) > 0) ids.add(String(id))
    for (const key of ['data', 'libro', 'libros', 'movimiento', 'movimientos', 'result']) {
      visit(value[key])
    }
    if (Array.isArray(value.ids)) value.ids.forEach(id => ids.add(String(id)))
  }
  visit(response?.data ?? response)
  return ids
}

// These templates return a model ID, and their accounting listings can contain
// generated descriptions rather than the submitted party/detail/account fields.
const receiptModels = new Set(['devolucion-ctacte-empresa', 'aporte-ctacte-empresa',
  'anticipo-sueldo', 'multa', 'ajuste-existencias', 'devolucion-de-impuestos'])

const identifyNewBalancedReceipt = ({ before, after, response, payload, model }) => {
  if (!receiptModels.has(model) || hasAccountingIds(response?.data ?? response) ||
    !getCreatedResponseIds(response).size || !payload?.fecha || !(Number(payload.monto) > 0)) return []
  const previous = new Set(before.map(rowIdentity))
  const oldReceipts = new Set(before.filter(row => row.comprobante != null).map(row => String(row.comprobante)))
  const groups = new Map()
  after.forEach(row => {
    if (previous.has(rowIdentity(row)) || row.comprobante == null || String(row.comprobante).trim() === '' ||
      oldReceipts.has(String(row.comprobante))) return
    const key = String(row.comprobante)
    groups.set(key, [...(groups.get(key) ?? []), row])
  })
  const expected = Math.abs(Number(payload.monto))
  const matches = [...groups.values()].filter(rows => {
    // Require both entries of a single new balanced receipt. Never select solely
    // by amount, or reuse an old receipt that acquired an additional entry.
    if (rows.length !== 2 || rows.some(row =>
      Number(row.folioCredito) !== folioByModel[model] ||
      String(row.fecha ?? '').slice(0, 10) !== String(payload.fecha).slice(0, 10))) return false
    const debit = rows.filter(row => Number(row.debe) === expected && Number(row.haber) === 0)
    const credit = rows.filter(row => Number(row.haber) === expected && Number(row.debe) === 0)
    if (debit.length !== 1 || credit.length !== 1 || rowIdentity(debit[0]) === rowIdentity(credit[0])) return false
    const accounts = rows.map(row => row.cuentaContableId ?? row.idCuenta ?? row.id_key).filter(id => id != null)
    return payload.cuentaContableId == null || !accounts.length || accounts.some(id => String(id) === String(payload.cuentaContableId))
  })
  return matches.length === 1 ? matches[0] : []
}

const identifyCreatedLinkages = ({ before, after, response, payload, model }) => {
  // These forms deliberately omit RUT. Require their party and description instead.
  const partyWithoutRut = ['multa', 'anticipo-sueldo', 'devolucion-de-impuestos'].includes(model) && !payload?.rut && Boolean(payload?.razonSocial && payload?.glosa)
  const accountWithoutRut = ['aporte-ctacte-empresa', 'devolucion-ctacte-empresa', 'ajuste-existencias'].includes(model) &&
    !payload?.rut && payload?.cuentaContableId != null && Boolean(payload?.glosa)
  const previousIds = new Set(before.map(rowIdentity))
  const newRows = after.filter(row => !previousIds.has(rowIdentity(row)))
  const responseIds = getCreatedResponseIds(response)
  let candidates = newRows
  if (responseIds.size) {
    const responseCandidates = hasAccountingIds(response?.data ?? response) ? after : newRows
    const matches = responseCandidates.filter(row => rowIds(row).some(id => responseIds.has(id)))
    if (matches.length && [...responseIds].every(id => matches.some(row => rowIds(row).includes(id)))) {
      return { rows: matches, byId: true }
    } else {
      // A generic creation ID is not an explicit accounting ID. Resolve it through
      // a new, unique record of the requested type from either listing, without
      // requiring the frontend-only _creationSource marker. Explicit accounting
      // IDs remain authoritative and cannot be replaced by a field match.
      if (hasAccountingIds(response?.data ?? response) || folioByModel[model] == null || (!payload?.rut && !partyWithoutRut && !accountWithoutRut)) return []
      candidates = newRows.filter(row => Number(row.folioCredito) === folioByModel[model])
    }
  }
  // APIs legacy sin ID: solo asociar una coincidencia inequívoca del formulario.
  if (!payload?.fecha || payload.monto == null) return []
  const normalize = value => String(value ?? '').trim().toLowerCase()
  const normalizeRut = value => normalize(value).replace(/[^0-9k]/g, '')
  const matchesPartyWithoutRut = row => {
    const party = row.razonSocial
    if (party && normalize(party) !== normalize(payload.razonSocial)) return false
    if (row.glosa && normalize(row.glosa) !== normalize(payload.glosa)) return false
    // A description-only listing can expose either the party or the detail.
    return Boolean(party || row.glosa ||
      (row.descripcion && [payload.razonSocial, payload.glosa].some(value => normalize(value) === normalize(row.descripcion))))
  }
  const matchesAccount = (row, form) => {
    const id = row.cuentaContableId ?? row.idCuenta ?? row.id_key
    if (id != null) return String(id) === String(form.cuentaContableId)
    const names = [row.nombreComprobante, row.nombreCodigoDocumento, row.nombreCuenta, row.nameCuenta].filter(Boolean)
    return Boolean(form.nombreComprobante) && names.some(name => normalize(name) === normalize(form.nombreComprobante))
  }
  const matches = candidates.filter(row => {
    if (folioByModel[model] != null && row.folioCredito != null && Number(row.folioCredito) !== folioByModel[model]) return false
    const sameRut = Boolean(payload.rut && row.rut && normalizeRut(row.rut) === normalizeRut(payload.rut))
    if (accountWithoutRut) {
      return Number(row.folioCredito) === folioByModel[model] &&
        matchesAccount(row, payload) &&
        normalize(row.glosa) === normalize(payload.glosa) &&
        String(row.fecha || '').slice(0, 10) === String(payload.fecha).slice(0, 10) &&
        Math.abs(Number(row.montoTotal ?? row.monto)) === Math.abs(Number(payload.monto))
    }
    if (partyWithoutRut) {
      // descripcion may contain the party name rather than the submitted glosa.
      // An available glosa must match; an omitted glosa needs the party match.
      return Number(row.folioCredito) === folioByModel[model] &&
        matchesPartyWithoutRut(row) &&
        String(row.fecha || '').slice(0, 10) === String(payload.fecha).slice(0, 10) &&
        Math.abs(Number(row.montoTotal ?? row.monto)) === Math.abs(Number(payload.monto))
    }
    const description = row._creationSource === 'vinculaciones' ? row.glosa : row.glosa || row.descripcion
    // When the list omits the description, require a matching RUT instead.
    const sameDescription = !payload.glosa || (description
      ? normalize(description) === normalize(payload.glosa)
      : sameRut)
    return String(row.fecha || '').slice(0, 10) === String(payload.fecha).slice(0, 10) &&
      Math.abs(Number(row.montoTotal ?? row.monto)) === Math.abs(Number(payload.monto)) &&
      sameDescription && (!payload.rut || sameRut)
  })
  return { rows: matches, byId: false }
}

// Identify the receipt first. Its debit and credit entries may describe different
// accounts and need not repeat the template type, description or total amount.
export const findCreatedLinkages = task => {
  const identified = identifyCreatedLinkages(task)
  const rows = identified.rows?.length ? identified.rows : identifyNewBalancedReceipt(task)
  if (!rows.length) return []
  const type = String(task.bankMovementType ?? '').trim().toLowerCase()
  if (!type) return identified.byId || rows.length === 1 ? rows : []
  if (!['abono', 'cargo'].includes(type)) return []

  const receiptKey = row => row.comprobante != null && String(row.comprobante).trim() !== ''
    ? 'receipt:' + String(row.comprobante)
    : rowIdentity(row)
  const groups = new Map()
  rows.forEach(row => {
    const key = receiptKey(row)
    groups.set(key, [...(groups.get(key) ?? []), row])
  })
  // Without returned IDs, matching multiple receipts is still ambiguous.
  if (!identified.byId && groups.size !== 1) return []
  const previous = new Set(task.before.map(rowIdentity))
  const identifiedIds = new Set(rows.map(rowIdentity))
  const result = []
  for (const [key] of groups) {
    const eligible = task.after.filter(row => {
      if (receiptKey(row) !== key) return false
      if (previous.has(rowIdentity(row)) && !identifiedIds.has(rowIdentity(row))) return false
      const debit = row.debe == null || row.debe === '' ? NaN : Number(row.debe)
      const credit = row.haber == null || row.haber === '' ? NaN : Number(row.haber)
      const amount = type === 'abono' ? debit : credit
      const opposite = type === 'abono' ? credit : debit
      if (!Number.isFinite(amount) || amount <= 0 || opposite !== 0) return false
      return task.payload?.monto == null || amount === Math.abs(Number(task.payload.monto))
    })
    if (eligible.length !== 1) return []
    result.push(eligible[0])
  }
  return result
}

// Export structural evidence only: never include RUT, party names or descriptions.
export const getCreatedLinkageDiagnostics = task => {
  const identified = identifyCreatedLinkages(task)
  const receiptRows = identified.rows?.length ? [] : identifyNewBalancedReceipt(task)
  const eligible = findCreatedLinkages(task)
  const previousIds = new Set(task.before.map(rowIdentity))
  const normalize = value => String(value ?? '').trim().toLowerCase()
  const has = value => value != null && value !== ''
  const rows = task.after.filter(row => !previousIds.has(rowIdentity(row)))
  return {
    version: 1,
    model: task.model ?? null,
    bankMovementType: task.bankMovementType ?? null,
    expectedFolio: folioByModel[task.model] ?? null,
    responseIds: [...getCreatedResponseIds(task.response)],
    explicitAccountingIds: hasAccountingIds(task.response?.data ?? task.response),
    counts: { before: task.before.length, after: task.after.length, new: rows.length },
    stage: eligible.length ? 'ready' : identified.rows?.length || receiptRows.length ? 'accounting-side' : 'identification',
    identificationMethod: identified.rows?.length ? identified.byId ? 'accounting-id' : 'form-fields' : receiptRows.length ? 'new-balanced-receipt' : null,
    identifiedIds: (identified.rows?.length ? identified.rows : receiptRows).map(row => row.idPrincipal ?? row.id),
    eligibleIds: eligible.map(row => row.idPrincipal ?? row.id),
    candidates: rows.slice(0, 30).map(row => ({
      id: row.id ?? null,
      idPrincipal: row.idPrincipal ?? null,
      comprobante: row.comprobante ?? null,
      folioCredito: row.folioCredito ?? null,
      source: row._creationSource ?? 'banco',
      fields: Object.keys(row).sort(),
      debit: has(row.debe) ? Number(row.debe) : null,
      credit: has(row.haber) ? Number(row.haber) : null,
      sameDate: String(row.fecha ?? '').slice(0, 10) === String(task.payload?.fecha ?? '').slice(0, 10),
      sameTotal: task.payload?.monto != null && Math.abs(Number(row.montoTotal ?? row.monto)) === Math.abs(Number(task.payload.monto)),
      sameDebit: task.payload?.monto != null && Number(row.debe) === Math.abs(Number(task.payload.monto)),
      sameCredit: task.payload?.monto != null && Number(row.haber) === Math.abs(Number(task.payload.monto)),
      hasParty: has(row.razonSocial),
      sameParty: has(row.razonSocial) && normalize(row.razonSocial) === normalize(task.payload?.razonSocial),
      hasDetail: has(row.glosa),
      sameDetail: has(row.glosa) && normalize(row.glosa) === normalize(task.payload?.glosa),
      descriptionMatchesParty: has(row.descripcion) && normalize(row.descripcion) === normalize(task.payload?.razonSocial),
      descriptionMatchesDetail: has(row.descripcion) && normalize(row.descripcion) === normalize(task.payload?.glosa),
      accountId: row.cuentaContableId ?? row.idCuenta ?? row.id_key ?? null,
      expectedAccountId: task.payload?.cuentaContableId ?? null,
      accountNameMatches: [row.nombreComprobante, row.nombreCodigoDocumento, row.nombreCuenta, row.nameCuenta]
        .some(name => has(name) && has(task.payload?.nombreComprobante) && normalize(name) === normalize(task.payload.nombreComprobante))
    }))
  }
}
